import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Building2, Palette, Save, ShieldCheck, MapPin, DollarSign } from "lucide-react";
import TwoFactorSetup from "@/components/TwoFactorSetup";

export default function AdminSettings() {
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/agencies/me")
      .then(({ data }) => setAgency(data))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payroll = agency.payroll || {};
      const { data } = await api.put("/agencies/me", {
        name: agency.name,
        address: agency.address || "",
        phone: agency.phone || "",
        email: agency.email || "",
        tax_id: agency.tax_id || "",
        default_tax_rate: Number(agency.default_tax_rate || 0),
        timezone: agency.timezone || "America/New_York",
        branding: agency.branding || {},
        geofence_enabled: !!agency.geofence_enabled,
        default_geofence_radius_m: Number(agency.default_geofence_radius_m || 150),
        medicaid_provider_id: (agency.medicaid_provider_id || "").trim(),
        payer_id: (agency.payer_id || "").trim(),
        default_service_code: (agency.default_service_code || "").trim() || "T1019",
        payroll: {
          weekly_overtime_threshold_h: Number(payroll.weekly_overtime_threshold_h || 40),
          overtime_multiplier: Number(payroll.overtime_multiplier || 1.5),
          holiday_multiplier: Number(payroll.holiday_multiplier || 1.5),
          mileage_rate_per_mile: Number(payroll.mileage_rate_per_mile || 0.67),
          week_start: payroll.week_start || "sun",
        },
      });
      setAgency(data);
      toast.success("Agency settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !agency) {
    return (
      <div className="flex items-center justify-center py-20 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const branding = agency.branding || {};
  const setBrand = (patch) => setAgency({ ...agency, branding: { ...branding, ...patch } });
  const payroll = agency.payroll || {};
  const setPayroll = (patch) => setAgency({ ...agency, payroll: { ...payroll, ...patch } });

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <div className="label-eyebrow">Settings</div>
        <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
          Agency settings
        </h1>
        <p className="mt-1 text-sm text-clinical-subtext">
          Manage your agency profile, branding, and defaults. Visible across all caregiver and family-portal views.
        </p>
      </div>

      <div className="card-surface p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-clinical-primary" />
          <h2 className="font-heading text-lg font-medium">Profile</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Agency name</label>
            <input
              value={agency.name || ""}
              onChange={(e) => setAgency({ ...agency, name: e.target.value })}
              className="input-clinical"
              data-testid="settings-name-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Address</label>
            <input
              value={agency.address || ""}
              onChange={(e) => setAgency({ ...agency, address: e.target.value })}
              className="input-clinical"
              data-testid="settings-address-input"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Phone</label>
            <input
              value={agency.phone || ""}
              onChange={(e) => setAgency({ ...agency, phone: e.target.value })}
              className="input-clinical"
              data-testid="settings-phone-input"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Billing email</label>
            <input
              type="email"
              value={agency.email || ""}
              onChange={(e) => setAgency({ ...agency, email: e.target.value })}
              className="input-clinical"
              data-testid="settings-email-input"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Tax ID / EIN</label>
            <input
              value={agency.tax_id || ""}
              onChange={(e) => setAgency({ ...agency, tax_id: e.target.value })}
              className="input-clinical"
              data-testid="settings-tax-id-input"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Default tax rate (%)</label>
            <input
              type="number"
              step="0.01"
              value={agency.default_tax_rate || 0}
              onChange={(e) => setAgency({ ...agency, default_tax_rate: e.target.value })}
              className="input-clinical"
              data-testid="settings-tax-rate-input"
            />
          </div>
        </div>
      </div>

      <div className="card-surface p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-clinical-primary" />
          <h2 className="font-heading text-lg font-medium">Branding</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow mb-1.5 block">Primary color</label>
            <input
              type="text"
              value={branding.primary_color || "#004AE6"}
              onChange={(e) => setBrand({ primary_color: e.target.value })}
              className="input-clinical"
              data-testid="settings-primary-color-input"
              placeholder="#004AE6"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Accent color</label>
            <input
              type="text"
              value={branding.accent_color || "#FF46A3"}
              onChange={(e) => setBrand({ accent_color: e.target.value })}
              className="input-clinical"
              data-testid="settings-accent-color-input"
              placeholder="#FF46A3"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Family portal logo URL</label>
            <input
              type="url"
              value={branding.family_portal_logo_url || ""}
              onChange={(e) => setBrand({ family_portal_logo_url: e.target.value })}
              className="input-clinical"
              data-testid="settings-logo-url-input"
              placeholder="https://… (paste a hosted PNG)"
            />
            <p className="text-[11px] text-clinical-subtext mt-1">
              Used on family-portal pages. "Powered by Afyanex" remains in the footer.
            </p>
          </div>
        </div>
      </div>

      <div className="card-surface p-5 space-y-4" data-testid="settings-geofence-card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-clinical-primary" />
            <h2 className="font-heading text-lg font-medium">Geofenced clock-in (EVV)</h2>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-clinical-subtext">Enable for this agency</span>
            <input
              type="checkbox"
              checked={!!agency.geofence_enabled}
              onChange={(e) => setAgency({ ...agency, geofence_enabled: e.target.checked })}
              className="h-4 w-4"
              data-testid="settings-geofence-toggle"
            />
          </label>
        </div>
        <p className="text-xs text-clinical-subtext">
          When enabled, caregivers must be physically within the configured radius of a client's
          home (set per-client) before they can clock in. Use this to satisfy the federal
          21st Century Cures Act EVV requirement for personal care services.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow mb-1.5 block">Default radius (meters)</label>
            <input
              type="number"
              min={50}
              max={2000}
              step={10}
              value={agency.default_geofence_radius_m ?? 150}
              onChange={(e) => setAgency({ ...agency, default_geofence_radius_m: Number(e.target.value) })}
              className="input-clinical"
              disabled={!agency.geofence_enabled}
              data-testid="settings-geofence-radius"
            />
            <p className="text-[11px] text-clinical-subtext mt-1">
              Used for any client without a custom radius. 150 m ≈ ½ city block.
            </p>
          </div>
          <div className="text-xs text-clinical-subtext bg-clinical-bg/60 border border-clinical-border rounded-lg p-3">
            <p className="font-medium text-clinical-text">How to set up each client</p>
            <p className="mt-1">
              Open the client's profile and enter the home's latitude and longitude
              (one-time setup). Future clock-ins from outside the radius will be blocked
              with a friendly error.
            </p>
          </div>
        </div>
      </div>

      <div className="card-surface p-5 space-y-4" data-testid="settings-evv-billing-card">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-clinical-primary" />
          <h2 className="font-heading text-lg font-medium">Medicaid / EVV billing identifiers</h2>
        </div>
        <p className="text-xs text-clinical-subtext">
          Required only if you submit visits to Sandata or HHAeXchange. Fill these in
          before exporting EVV files from the{" "}
          <a href="/admin/evv-export" className="text-clinical-primary hover:underline">
            EVV Export
          </a>{" "}
          page.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow mb-1.5 block">Medicaid Provider ID</label>
            <input
              value={agency.medicaid_provider_id || ""}
              onChange={(e) => setAgency({ ...agency, medicaid_provider_id: e.target.value })}
              className="input-clinical"
              placeholder="e.g. Sandata BusinessEntityID"
              data-testid="settings-medicaid-provider-id-input"
            />
            <p className="text-[11px] text-clinical-subtext mt-1">
              Issued by your state Medicaid program. Sandata calls this "BusinessEntityID".
            </p>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Default Payer ID</label>
            <input
              value={agency.payer_id || ""}
              onChange={(e) => setAgency({ ...agency, payer_id: e.target.value })}
              className="input-clinical"
              placeholder="e.g. NY-MMIS"
              data-testid="settings-payer-id-input"
            />
            <p className="text-[11px] text-clinical-subtext mt-1">
              Used as fallback when a client doesn't have a payer set. Required for HHAeXchange.
            </p>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Default service code (HCPCS)</label>
            <input
              value={agency.default_service_code || ""}
              onChange={(e) => setAgency({ ...agency, default_service_code: e.target.value })}
              className="input-clinical"
              placeholder="T1019"
              data-testid="settings-service-code-input"
            />
            <p className="text-[11px] text-clinical-subtext mt-1">
              T1019 = personal care attendant (most common). G0156, S5125 are other defaults.
            </p>
          </div>
        </div>
      </div>

      <div className="card-surface p-5 space-y-4" data-testid="settings-payroll-card">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-clinical-primary" />
          <h2 className="font-heading text-lg font-medium">Payroll rules</h2>
        </div>
        <p className="text-xs text-clinical-subtext">
          Applied when calculating timecards on the <a href="/admin/timecards" className="text-clinical-primary hover:underline">Timecards</a> page.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow mb-1.5 block">Weekly overtime threshold (hrs)</label>
            <input
              type="number"
              step="0.5"
              min={0}
              value={payroll.weekly_overtime_threshold_h ?? 40}
              onChange={(e) => setPayroll({ weekly_overtime_threshold_h: e.target.value })}
              className="input-clinical"
              data-testid="settings-ot-threshold"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Overtime multiplier</label>
            <input
              type="number"
              step="0.05"
              min={1}
              value={payroll.overtime_multiplier ?? 1.5}
              onChange={(e) => setPayroll({ overtime_multiplier: e.target.value })}
              className="input-clinical"
              data-testid="settings-ot-multiplier"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Holiday multiplier</label>
            <input
              type="number"
              step="0.05"
              min={1}
              value={payroll.holiday_multiplier ?? 1.5}
              onChange={(e) => setPayroll({ holiday_multiplier: e.target.value })}
              className="input-clinical"
              data-testid="settings-holiday-multiplier"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Mileage rate ($/mi)</label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={payroll.mileage_rate_per_mile ?? 0.67}
              onChange={(e) => setPayroll({ mileage_rate_per_mile: e.target.value })}
              className="input-clinical"
              data-testid="settings-mileage-rate"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Week starts on</label>
            <div className="flex gap-2">
              {["sun", "mon"].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setPayroll({ week_start: d })}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${
                    (payroll.week_start || "sun") === d
                      ? "border-clinical-primary bg-clinical-primary/5 text-clinical-primary"
                      : "border-clinical-border bg-white text-clinical-text"
                  }`}
                  data-testid={`settings-week-start-${d}`}
                >
                  {d === "sun" ? "Sunday" : "Monday"}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-clinical-border bg-clinical-bg p-4 flex items-start gap-2 text-xs text-clinical-subtext">
        <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-clinical-primary" />
        <div>
          Your agency ID is <code className="text-clinical-text">{agency.id}</code>.
          All your caregiver and client data is isolated to this tenant —
          no other agency can read it.
        </div>
      </div>

      <TwoFactorSetup />

      <div className="sticky bottom-0 bg-clinical-bg/80 backdrop-blur-sm py-3 -mx-6 px-6 border-t border-clinical-border">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="settings-save-button"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <>
              <Save className="h-4 w-4" /> Save settings
            </>
          )}
        </button>
      </div>
    </div>
  );
}
